import { useEffect, useState } from "react";
import { Bookmark, Download, Route, Trash2, Users } from "lucide-react";
import { http, defaultStatuses, loanTypes, notify, can, money } from "../api";
import { PanelHead, Loading, Modal } from "./common";

const buildQuery = (f) => {
  const params = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
  const s = params.toString();
  return s ? `?${s}` : "";
};

const EMPTY_FILTERS = {
  date_from: "", date_to: "", status: "", loan_type: "",
  bank_name: "", team_id: "", telecaller_id: "", product_id: "", reason: "",
};

export default function Reports({ user }) {
  const [data, setData] = useState(null);
  const [banks, setBanks] = useState([]);
  const [products, setProducts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const [routing, setRouting] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    http.get(`/reports${buildQuery(filters)}`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((err) => {
        const msg = err.response?.data?.detail || "Reports are not available right now";
        setError(msg);
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters]);

  useEffect(() => {
    http.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
    http.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    http.get("/reports/views").then((r) => setViews(r.data)).catch(() => {});
    if (["admin", "business_head", "sales_manager", "back_office_manager"].includes(user.role)) {
      http.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
      http.get("/users").then((r) => setMembers(r.data)).catch(() => {});
    }
  }, [user.role]);

  const setFilter = (k, v) => { setFilters((prev) => ({ ...prev, [k]: v })); setActiveView(null); };
  const reset = () => { setFilters(EMPTY_FILTERS); setActiveView(null); };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const saveCurrentView = async () => {
    if (!activeFilterCount) {
      notify.info("Pick at least one filter before saving a view");
      return;
    }
    setSaveOpen(true);
  };

  const persistView = async ({ name, share }) => {
    try {
      const r = await http.post("/reports/views", {
        name: name.trim(),
        filters,
        shared_with_team: share,
      });
      setViews((prev) => [r.data, ...prev]);
      setActiveView(r.data.view_id);
      setSaveOpen(false);
      notify.success(share ? "View saved and shared with your team" : "View saved");
    } catch (err) {
      notify.error(err, "Could not save view");
    }
  };

  const applyView = (view) => {
    setFilters({ ...EMPTY_FILTERS, ...(view.filters || {}) });
    setActiveView(view.view_id);
  };

  const deleteView = async (view) => {
    if (!view.mine && user.role !== "admin") {
      notify.info(`Only ${view.user_name || "the creator"} or an admin can delete this shared view`);
      return;
    }
    if (!window.confirm(`Delete saved view "${view.name}"?${view.shared ? " This will also un-share it with the team." : ""}`)) return;
    try {
      await http.delete(`/reports/views/${view.view_id}`);
      setViews((prev) => prev.filter((v) => v.view_id !== view.view_id));
      if (activeView === view.view_id) setActiveView(null);
      notify.success("View deleted");
    } catch (err) {
      notify.error(err, "Could not delete view");
    }
  };

  const canShareViews = ["admin", "business_head", "sales_manager", "back_office_manager"].includes(user.role);
  const canSeeRouting = can(user, "view_reports");

  useEffect(() => {
    if (tab !== "routing" || !canSeeRouting) return;
    const q = buildQuery({
      date_from: filters.date_from, date_to: filters.date_to,
      bank_name: filters.bank_name, reason: filters.reason,
    });
    http.get(`/reports/routing${q}`)
      .then((r) => setRouting(r.data))
      .catch((err) => notify.error(err, "Could not load routing audit"));
  }, [tab, canSeeRouting, filters.date_from, filters.date_to, filters.bank_name, filters.reason]);

  const exportCsv = async (kind) => {
    try {
      const r = await http.get(`/reports/export${buildQuery({ ...filters, kind })}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `dsa-${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notify.error(err, "Export failed");
    }
  };

  if (!data && loading) return <Loading>Preparing report canvas…</Loading>;

  const showTeamFilter = ["admin", "business_head"].includes(user.role);
  const showTelecallerFilter = ["admin", "business_head", "sales_manager"].includes(user.role);
  const canExport = can(user, "export_data");

  return (
    <>
      <div className="page-intro">
        <div>
          <p className="eyebrow">ANALYTICS / PERFORMANCE</p>
          <h1>Reports</h1>
          <p className="muted">A filtered view of your team’s operating rhythm.</p>
        </div>
        {canExport && (
          <div className="detail-actions">
            <button data-testid="reports-export-leads" className="outline-button" onClick={() => exportCsv("leads")}>
              <Download size={14} /> Leads CSV
            </button>
            <button data-testid="reports-export-apps" className="outline-button" onClick={() => exportCsv("applications")}>
              <Download size={14} /> Applications CSV
            </button>
          </div>
        )}
      </div>

      {error && !data ? (
        <section className="panel" data-testid="reports-error">
          <PanelHead title="Reports unavailable" />
          <p className="empty compact-empty">
            {error}
          </p>
          <p className="muted small">
            {can(user, "view_reports")
              ? "Retry in a moment or check your connection."
              : "This view is restricted by your role. Go back to your workspace overview to continue."}
          </p>
        </section>
      ) : (
        <>
      {canSeeRouting && (
        <div className="admin-tabs" data-testid="report-tabs">
          <button
            data-testid="report-tab-overview"
            className={tab === "overview" ? "active" : ""}
            onClick={() => setTab("overview")}
          >
            Overview
          </button>
          <button
            data-testid="report-tab-routing"
            className={tab === "routing" ? "active" : ""}
            onClick={() => setTab("routing")}
          >
            <Route size={13} /> Routing audit
          </button>
        </div>
      )}

      <section className="report-filters" data-testid="report-filters">
        <label>
          From
          <input
            data-testid="report-filter-from"
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilter("date_from", e.target.value)}
          />
        </label>
        <label>
          To
          <input
            data-testid="report-filter-to"
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilter("date_to", e.target.value)}
          />
        </label>
        {tab !== "routing" && (
          <label>
            Status
            <select
              data-testid="report-filter-status"
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
            >
              <option value="">All</option>
              {defaultStatuses.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        )}
        {tab !== "routing" && (
          <label>
            Loan type
            <select
              data-testid="report-filter-loan_type"
              value={filters.loan_type}
              onChange={(e) => setFilter("loan_type", e.target.value)}
            >
              <option value="">All</option>
              {loanTypes.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        )}
        <label>
          Bank / NBFC
          <select
            data-testid="report-filter-bank"
            value={filters.bank_name}
            onChange={(e) => setFilter("bank_name", e.target.value)}
          >
            <option value="">All</option>
            {banks.map((b) => <option key={b.bank_id} value={b.name}>{b.name}</option>)}
          </select>
        </label>
        {tab !== "routing" && (
          <label>
            Product
            <select
              data-testid="report-filter-product"
              value={filters.product_id}
              onChange={(e) => setFilter("product_id", e.target.value)}
            >
              <option value="">All</option>
              {products
                .filter((p) => !filters.bank_name || p.bank_name === filters.bank_name)
                .map((p) => (
                  <option key={p.product_id} value={p.product_id}>
                    {p.name}{p.bank_name ? ` · ${p.bank_name}` : ""}
                  </option>
                ))}
            </select>
          </label>
        )}
        {tab === "routing" && (
          <label>
            Reason
            <select
              data-testid="report-filter-reason"
              value={filters.reason}
              onChange={(e) => setFilter("reason", e.target.value)}
            >
              <option value="">All</option>
              <option value="auto">Auto-routed</option>
              <option value="fallback">Fallback → BOM</option>
              <option value="manual">Manual override</option>
              <option value="application">Per-application</option>
            </select>
          </label>
        )}
        {tab !== "routing" && showTeamFilter && (
          <label>
            Sales team
            <select
              data-testid="report-filter-team"
              value={filters.team_id}
              onChange={(e) => setFilter("team_id", e.target.value)}
            >
              <option value="">All</option>
              {teams.filter((t) => t.team_type === "sales").map((t) => (
                <option key={t.team_id} value={t.team_id}>{t.name}</option>
              ))}
            </select>
          </label>
        )}
        {tab !== "routing" && showTelecallerFilter && (
          <label>
            Telecaller
            <select
              data-testid="report-filter-telecaller"
              value={filters.telecaller_id}
              onChange={(e) => setFilter("telecaller_id", e.target.value)}
            >
              <option value="">All</option>
              {members.filter((m) => m.role === "telecaller").map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>
        )}
        <button className="outline-button" onClick={reset} data-testid="reports-filter-reset">
          Reset
        </button>
        {tab !== "routing" && (
          <button
            className="outline-button"
            onClick={saveCurrentView}
            data-testid="reports-save-view-button"
            disabled={!activeFilterCount}
            title={activeFilterCount ? "Save this filter combination for later" : "Pick a filter first"}
          >
            <Bookmark size={14} /> Save view
          </button>
        )}
      </section>

      {views.length > 0 && (
        <section className="saved-views" data-testid="saved-views-bar">
          <span className="eyebrow">SAVED VIEWS</span>
          {views.map((v) => (
            <span
              key={v.view_id}
              className={[
                "view-chip",
                activeView === v.view_id && "active",
                v.shared && "shared",
              ].filter(Boolean).join(" ")}
              data-testid={`saved-view-${v.view_id}`}
              title={v.shared ? `Shared by ${v.user_name} with ${v.shared_team_name}` : `Personal view`}
            >
              <button
                type="button"
                onClick={() => applyView(v)}
                data-testid={`saved-view-apply-${v.view_id}`}
              >
                {v.shared && <Users size={11} data-testid={`saved-view-shared-badge-${v.view_id}`} />}
                {v.name}
              </button>
              {(v.mine || user.role === "admin") && (
                <button
                  type="button"
                  className="chip-close"
                  onClick={() => deleteView(v)}
                  aria-label={`Delete ${v.name}`}
                  data-testid={`saved-view-delete-${v.view_id}`}
                >
                  <Trash2 size={11} />
                </button>
              )}
            </span>
          ))}
        </section>
      )}

      {saveOpen && (
        <SaveViewModal
          canShare={canShareViews}
          onClose={() => setSaveOpen(false)}
          onSave={persistView}
        />
      )}

      {tab === "routing" && canSeeRouting ? (
        <RoutingPanel data={routing} filters={filters} setFilter={setFilter} />
      ) : !data ? (
        <Loading>Loading…</Loading>
      ) : (
        <>
          <div className="report-kpis">
            <div><span>Lead to application</span><strong>{data.totals.conversion}%</strong></div>
            <div><span>Approval ratio</span><strong>{data.totals.approval_ratio}%</strong></div>
            <div><span>Total leads</span><strong>{data.totals.leads}</strong></div>
            <div><span>Applications</span><strong>{data.totals.applications}</strong></div>
          </div>

          <section className="panel payout-panel">
            <PanelHead title="Partner payouts" />
            <div className="payout-summary">
              <div>
                <span>Total payout logged</span>
                <strong data-testid="report-commission-total">
                  {money(data.totals.commission_total)}
                </strong>
              </div>
              <div>
                <span>Invoiced &amp; pending</span>
                <strong className="amber-text" data-testid="report-commission-pending">
                  {money(data.totals.commission_pending)}
                </strong>
              </div>
              <div>
                <span>Received</span>
                <strong className="green-text" data-testid="report-commission-received">
                  {money(data.totals.commission_received)}
                </strong>
              </div>
            </div>
            {(data.commission_by_bank || []).length > 0 && (
              <table className="tight-table">
                <thead>
                  <tr>
                    <th>Partner bank</th>
                    <th>Total payout</th>
                    <th>From disbursements</th>
                    <th>Invoiced</th>
                    <th>Received</th>
                  </tr>
                </thead>
                <tbody>
                  {data.commission_by_bank.map((r) => (
                    <tr key={r.label} data-testid={`payout-row-${r.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      <td><strong>{r.label}</strong></td>
                      <td>{money(r.value)}</td>
                      <td>{money(r.disbursed)}</td>
                      <td className="amber-text">{money(r.invoiced)}</td>
                      <td className="green-text">{money(r.received)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <div className="reports-grid">
            <ReportChart title="Lead status" items={data.lead_status} />
            <ReportChart title="Applications by status" items={data.applications} />
            <ReportChart title="Loan type mix" items={data.loan_types} />
            <ReportChart title="Bank / NBFC" items={data.banks} />
            <ReportChart title="Sales team" items={data.team_performance} />
          </div>

          {data.telecaller_performance?.length > 0 && (
            <section className="panel">
              <PanelHead title="Telecaller performance" />
              <table className="tight-table">
                <thead>
                  <tr>
                    <th>Telecaller</th>
                    <th>Login Status</th>
                    <th>Applications</th>
                    <th>Approved</th>
                    <th>Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.telecaller_performance.map((r) => (
                    <tr key={r.label}>
                      <td><strong>{r.label}</strong></td>
                      <td>{r.leads}</td>
                      <td>{r.applications}</td>
                      <td>{r.approved}</td>
                      <td>{r.leads ? Math.round((r.applications / r.leads) * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
      </>
      )}
    </>
  );
}

function ReportChart({ title, items }) {
  const max = Math.max(...(items || []).map((x) => x.value), 1);
  return (
    <section className="panel report-card">
      <PanelHead title={title} />
      {(items || []).length ? items.map((x) => (
        <div className="chart-row" key={x.label}>
          <span>{x.label}</span>
          <div><i style={{ width: `${(x.value / max) * 100}%` }} /></div>
          <b>{x.value}</b>
        </div>
      )) : <div className="empty compact-empty">No data.</div>}
    </section>
  );
}

function SaveViewModal({ canShare, onClose, onSave }) {
  const [name, setName] = useState("");
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    await onSave({ name, share });
    setBusy(false);
  };

  return (
    <Modal title="Save this report view" onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <label>
          View name
          <input
            data-testid="save-view-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q1 approved leads"
            required
            autoFocus
          />
        </label>
        {canShare && (
          <label className="checkbox-row">
            <input
              type="checkbox"
              data-testid="save-view-share-toggle"
              checked={share}
              onChange={(e) => setShare(e.target.checked)}
            />
            <span>
              <strong>Share with my team</strong>
              <small>Everyone on your team with report access will see this view in their Saved views bar.</small>
            </span>
          </label>
        )}
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary-button" disabled={busy || !name.trim()} data-testid="save-view-submit">
            {busy ? "Saving…" : share ? "Save & share" : "Save view"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


const REASON_LABELS = {
  auto: "Auto-routed",
  fallback: "Fallback → BOM",
  manual: "Manual override",
  application: "Per-application",
};

function RoutingPanel({ data, filters, setFilter }) {
  if (!data) return <Loading>Loading routing audit…</Loading>;
  const t = data.totals || {};
  const reasons = [
    ["auto", "Auto", t.auto || 0, ""],
    ["fallback", "Fallback", t.fallback || 0, "amber-text"],
    ["manual", "Manual", t.manual || 0, ""],
    ["application", "Per-application", t.application || 0, ""],
  ];
  return (
    <>
      <div className="report-kpis" data-testid="routing-totals">
        {reasons.map(([key, label, value, cls]) => (
          <button
            key={key}
            type="button"
            className={"kpi-tile" + (filters.reason === key ? " active" : "")}
            onClick={() => setFilter("reason", filters.reason === key ? "" : key)}
            data-testid={`routing-kpi-${key}`}
          >
            <span>{label}</span>
            <strong className={cls}>{value}</strong>
          </button>
        ))}
      </div>
      <section className="panel">
        <PanelHead title="Routing decisions" subtitle="Every lead handoff, most recent first" />
        {data.events.length === 0 ? (
          <div className="empty compact-empty">No routing events matched your filters.</div>
        ) : (
          <table className="tight-table" data-testid="routing-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Bank</th>
                <th>Routed to</th>
                <th>Reason</th>
                <th>Note</th>
                <th>Actor</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e, i) => (
                <tr key={`${e.lead_id}-${e.at}-${i}`} data-testid={`routing-row-${e.lead_id}-${i}`}>
                  <td>
                    <strong>{e.customer_name || "—"}</strong>
                    <br /><small className="mono">{e.lead_id}</small>
                  </td>
                  <td>{e.bank_name || "—"}</td>
                  <td>
                    {e.routed_to}
                    {e.back_office_team_name && <><br /><small className="muted">{e.back_office_team_name}</small></>}
                  </td>
                  <td>
                    <span className={`reason-tag reason-${e.reason}`}>
                      {REASON_LABELS[e.reason] || e.reason}
                    </span>
                  </td>
                  <td className="muted small">{e.note}</td>
                  <td>{e.actor}</td>
                  <td className="muted small">{(e.at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
